import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, AlertCircle, Loader2, ArrowRight } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState({ status: "polling", message: "Confirming your payment…" });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "No session id provided." });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          if (!cancelled) setState({ status: "success", message: "Payment confirmed." });
          return;
        }
        if (data.status === "expired") {
          if (!cancelled) setState({ status: "error", message: "Checkout session expired. Please try again." });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          if (!cancelled) setState({ status: "timeout", message: "We're still confirming your payment. It may take a moment — refresh in a few seconds." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (!cancelled) setState({ status: "error", message: e.response?.data?.detail || "Couldn't verify payment." });
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-[#0A1128] pt-32 pb-20">
      <div className="max-w-xl mx-auto px-6 text-center" data-testid="payment-success-page">
        {state.status === "success" ? (
          <>
            <CheckCircle2 className="w-16 h-16 text-[#7DA3D9] mx-auto mb-6" data-testid="payment-success-icon" />
            <div className="overline mb-2">Welcome to Complete Access</div>
            <h1 className="font-display text-4xl lg:text-5xl">You're all set.</h1>
            <p className="text-slate-300 mt-4 leading-relaxed">
              Every section of OSRR is now unlocked — the full directory, every Rights Hub, every territory.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link to="/agencies" className="btn-gold inline-flex items-center gap-2" data-testid="go-directory">
                Open the directory <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/dashboard" className="btn-ghost-gold inline-flex items-center gap-2">
                Go to dashboard
              </Link>
            </div>
          </>
        ) : state.status === "polling" ? (
          <>
            <Loader2 className="w-12 h-12 text-[#7DA3D9] mx-auto mb-6 animate-spin" data-testid="payment-polling-icon" />
            <h1 className="font-display text-3xl">Confirming your payment</h1>
            <p className="text-slate-400 mt-3">{state.message}</p>
          </>
        ) : (
          <>
            <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-6" data-testid="payment-error-icon" />
            <h1 className="font-display text-3xl">Almost there…</h1>
            <p className="text-slate-300 mt-3">{state.message}</p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link to="/upgrade" className="btn-ghost-gold inline-flex items-center gap-2" data-testid="payment-retry">
                Back to upgrade
              </Link>
              <Link to="/dashboard" className="btn-gold inline-flex items-center gap-2">
                Dashboard
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
